import { SpinSpeedType } from "@westpierstudio/core-module-oryx/components/ui/slot-game-ui/UIStateMachine";
import { Point } from "@westpierstudio/luma/layout/DefaultLayout";
import { Model } from "@westpierstudio/luma/mvc";
import { IServiceProvider } from "@westpierstudio/luma/services";
import { clone, cloneDeep, over, random, times } from "lodash";
import { FreeSpinEvent } from "servertypes/gameevents/FreeSpin";
import { SmallSpinEvent } from "servertypes/gameevents/SmallSpin";
import { MultipliedWildsData, SpinEvent } from "servertypes/gameevents/Spin";
import { VSSpinEvent } from "servertypes/gameevents/VSSpin";
import { GameRequest } from "servertypes/ServerEvents";
import { BonusTriggeringState, JackpotID, LastEventData, MultipliedWinsMap, OverlaySymbolData, OverlaySymbols, PayLineWin, ReelSetOverlays, ReelSetSymbols,
    SymbolPos } from "servertypes/SharedTypes";
import { MinimumHeightSpec } from "utils/Responsive";

export enum ReelStopEase {
    LINEAR = `linear`,
    SINE_IN = `sineIn`,
    SINE_OUT = `sineOut`,
    SINE_IN_OUT = `sineInOut`,
    QUAD_IN = `quadIn`,
    QUAD_OUT = `quadOut`,
    QUAD_IN_OUT = `quadInOut`,
    CUBIC_IN = `cubicIn`,
    CUBIC_OUT = `cubicOut`,
    CUBIC_IN_OUT = `cubicInOut`,
    QUART_IN = `quartIn`,
    QUART_OUT = `quartOut`,
    QUART_IN_OUT = `quartInOut`,
    QUINT_IN = `quintIn`,
    QUINT_OUT = `quintOut`,
    QUINT_IN_OUT = `quintInOut`,
    CIRC_IN = `circIn`,
    CIRC_OUT = `circOut`,
    CIRC_IN_OUT = `circInOut`
};

interface SpinSettings {
    symbolsPerSecond: number,
    baseSpinTime: number,
    anticipationTime: number,
    reelStopDelay: number,
    reelStopDistance: number,
    reelStopTimeIn: number,
    reelStopTimeOut: number,
    reelStopEaseIn: ReelStopEase,
    reelStopEaseOut: ReelStopEase,
    instantSlam: boolean,
    logSpinTime: boolean
}

export interface SpinConfig extends SpinSettings {
    anticipationActive: boolean
}

interface ReelsSpec {
    x: number,
    y: number,
    portrait : MinimumHeightSpec[]
    dragonsReels: Point,
    pandasReels: Point,
    reelSpacing: number,
    symbolSize: {width: number, height: number},
    mask: {
        x: number,
        y: number,
        width: number,
        height: number
    },
    dragonsBackground: Record<string, any>,
    pandasBackground: Record<string, any>,
    dragonsFrame: Record<string, any>,
    pandasFrame: Record<string, any>
}

type ReelSetPositions = [Point, Point, Point, Point];

export interface ReelsLayout extends ReelsSpec {
    globalSymbolPositions: Point[][],
    setPositions: ReelSetPositions, //reel positions relative to set origin
    reelCount: number,
    reelsPerSet: number,
    symbolCount: number, //visible count
}

export type OverlaySymbolState = {dragons: ReelSetOverlays, pandas: ReelSetOverlays};
export type JumpingWildsSet = {source: SymbolPos, target: SymbolPos, multiplier: number}[];
export type BonusSymbolPositions = {dragons: SymbolPos[], pandas: SymbolPos[]};
export type BonusTriggeringStateData = {
    freeGamesState?: BonusTriggeringStateData,
    oldSymbols: {dragons: ReelSetSymbols, pandas: ReelSetSymbols},
    symbols: {dragons: ReelSetSymbols, pandas: ReelSetSymbols},
    overlays: OverlaySymbolState,
    paylines: {dragons: PayLineWin[], pandas: PayLineWin[]},
    multipliedWinsMap: {dragons?: MultipliedWinsMap, pandas?: MultipliedWinsMap},
    multipliedWilds: {dragons: MultipliedWildsData, pandas: MultipliedWildsData}
}

type MultipliedWilds = {
    position: SymbolPos,
    count: number
}[]

export const enum SpinAction {
    SPIN = `SPIN`,
    VS_SPIN = `VS_SPIN`,
    FREE_SPIN = `FREE_SPIN`
}

export const enum SpinType {
    REGULAR = `regular`,
    VS = `vs`,
    FREE_SPIN = `freespin`,
    TURBO = `turbo`,
    TURBO_VS = `turbovs`,
    TURBO_FREE_SPIN = `turbofreespin`
}

export class ReelsModel extends Model {
    private defaultSpinConfigs: Map<SpinType, SpinConfig> = new Map();
    private _activeSpinConfig!: SpinConfig;
    private _bonusTriggeringState!: BonusTriggeringStateData;
    dragonsSpinResult?: ReelSetSymbols;
    pandasSpinResult?: ReelSetSymbols;
    dragonsVSSpinResults: ReelSetSymbols[] = [];
    pandasVSSpinResults: ReelSetSymbols[] = [];
    overlaySymbolState = this.overlaySymbolEmptyState;
    scatterOverlaySymbols?: OverlaySymbolData[];
    jumpingWilds = {dragons: [] as JumpingWildsSet, pandas: [] as JumpingWildsSet};
    jackpotSymbols?: BonusSymbolPositions;
    vsSymbols?: BonusSymbolPositions;
    freeSpinSymbols?: BonusSymbolPositions;
    doubleBonus: boolean = false;
    winningSymbolState: boolean[][] = [];
    spinConfigs!: Map<SpinType, SpinConfig>;
    layout!: ReelsLayout;
    startTime = 0;

    constructor(services: IServiceProvider) {
        super(services);

        this.setupSpinConfigs();
    }

    get overlaySymbolEmptySet(): ReelSetOverlays {
        return [
            [`E`,`E`,`E`,`E`],
            [`E`,`E`,`E`,`E`],
            [`E`,`E`,`E`,`E`],
            [`E`,`E`,`E`,`E`]
        ];
    }

    get overlaySymbolEmptyState(): OverlaySymbolState {
        return {
            dragons: this.overlaySymbolEmptySet,
            pandas: this.overlaySymbolEmptySet
        };
    }

    get activeSpinConfig(): SpinConfig {
        return this._activeSpinConfig;
    }

    get bonusTriggeringState(): BonusTriggeringStateData {
        return this._bonusTriggeringState;
    }

    getBonusOverlaySymbolPositions(freeGames: boolean = false): BonusSymbolPositions {

        const overlayState = freeGames ? this._bonusTriggeringState.freeGamesState?.overlays : this._bonusTriggeringState.overlays;

        if (!overlayState) return {dragons: [], pandas: []};

        const dragons: SymbolPos[] = [];
        const pandas: SymbolPos[] = [];

        for (let i = 0; i < overlayState.dragons.length; i++) {

            const column = overlayState.dragons[i];
            for (let j = 0; j < column.length; j++) {

                if (overlayState.dragons[i][j] === `E`) continue;

                dragons.push({
                    column: i,
                    row: j
                });
            }
        }

        for (let i = 0; i < overlayState.pandas.length; i++) {

            const column = overlayState.pandas[i];
            for (let j = 0; j < column.length; j++) {

                if (overlayState.pandas[i][j] === `E`) continue;

                pandas.push({
                    column: i,
                    row: j
                });
            }
        }

        return {
            dragons: dragons,
            pandas: pandas
        };
    }

    getSettingsForConfig(config: SpinConfig): SpinSettings {

        const settings: Record<SpinType, SpinSettings> = this.services.resources.getSetting(`reelSpinSettings`);
        const spinSettings = settings[SpinType.REGULAR];

        const out: Record<string,any> = {};

        for (const key of Object.keys(spinSettings)) {
            out[key] = config[key as keyof SpinSettings];
        }

        return out as SpinSettings;
    }

    getSpinType(spinAction: SpinAction, spinSpeedType: SpinSpeedType): SpinType {

        let out: SpinType;

        switch(spinAction) {
            case SpinAction.SPIN:
                out = (spinSpeedType === SpinSpeedType.REGULAR) ? SpinType.REGULAR : SpinType.TURBO;
                break;
            case SpinAction.VS_SPIN:
                out = (spinSpeedType === SpinSpeedType.REGULAR) ? SpinType.VS : SpinType.TURBO_VS;
                break;
            case SpinAction.FREE_SPIN:
                out = (spinSpeedType === SpinSpeedType.REGULAR) ? SpinType.FREE_SPIN : SpinType.TURBO_FREE_SPIN;
                break;
        }

        return out;
    }

    getOverlayResultForReel(reelIndex: number): OverlaySymbols {

        const {reelsPerSet} = this.layout;

        const set = (reelIndex < reelsPerSet) ? this.overlaySymbolState.dragons : this.overlaySymbolState.pandas;
        reelIndex = reelIndex % reelsPerSet;

        return set[reelIndex];
    }

    setupLayout(): ReelsLayout {

        const spec: ReelsSpec = cloneDeep(this.services.resources.getSpec(`reels`));

        const {reelsPerSet, symbolCount} = this.services.resources.getSetting(`reels`);

        const setPositions = this.calcReelPositionsForSet(spec, reelsPerSet);

        const dragonsSymbolPositions = this.calcSymbolPositionsForSet(spec, spec.dragonsReels, reelsPerSet, symbolCount, setPositions);
        const pandasSymbolPositions = this.calcSymbolPositionsForSet(spec, spec.pandasReels, reelsPerSet, symbolCount, setPositions);

        this.layout = {
            ...spec,
            globalSymbolPositions: dragonsSymbolPositions.concat(pandasSymbolPositions),
            setPositions,
            reelCount: reelsPerSet * 2,
            reelsPerSet,
            symbolCount
        };

        return this.layout;
    }

    setSpinConfigForSpinType(type: SpinType, anticipation: boolean): SpinConfig {

        this._activeSpinConfig = this.spinConfigs.get(type) as SpinConfig;
        this._activeSpinConfig.anticipationActive = anticipation && !this.activeSpinConfig.instantSlam;

        return this._activeSpinConfig;
    }

    updateSpinConfig(type: SpinType, config: SpinConfig): void {
        this.spinConfigs.set(type, config);
    }

    restoreDefaultSpinConfigs(): void {
        this.spinConfigs = cloneDeep(this.defaultSpinConfigs);
    }

    processLastEventData(data: LastEventData): void {

        const {gameplay, nextActions} = data;
        const {overlaySymbols} = gameplay;

        this.setOverlaySymbolState(overlaySymbols);
        this.setVSSymbols(overlaySymbols, nextActions[0]);
    }

    processSpinEvent(event: SpinEvent | SmallSpinEvent): void {

        const {gameplay, nextActions} = event.body.data;
        const {dragonsReels, oldDragonsReels, pandasReels, oldPandasReels, dragonsPaylineWins, pandasPaylineWins,
               overlaySymbols, jackpotSymbol, dragonsWilds, pandasWilds} = gameplay;

        this.doubleBonus = false;

        //Set pre jumping wilds result
        this.dragonsSpinResult = oldDragonsReels[0];
        this.pandasSpinResult = oldPandasReels[0];

        this.setOverlaySymbolState(overlaySymbols);
        this.setJumpingWilds(oldDragonsReels[0], dragonsReels, oldPandasReels[0], pandasReels, dragonsWilds, pandasWilds);
        this.setJackpotSymbols(overlaySymbols, jackpotSymbol);
        this.setVSSymbols(overlaySymbols, (nextActions) ? nextActions[0] : undefined);
        this.setFreeSpinSymbols(overlaySymbols, (nextActions) ? nextActions[0] : undefined);
        this.setWinningSymbolState(dragonsPaylineWins, pandasPaylineWins);
    }

    processVSSpinEvent(event: VSSpinEvent, haveEnteredFreeSpins: boolean): void {

        const {gameplay, nextActions} = event.body.data;
        const {dragonsReels, pandasReels, oldDragonsReels, oldPandasReels, overlaySymbols, jackpotSymbol, bonusTriggeringState} = gameplay;

        if (bonusTriggeringState) {
            this.setBonusTriggeringState(bonusTriggeringState);
        }

        this.setVSResults(dragonsReels, pandasReels, oldDragonsReels, oldPandasReels);
        this.setVSOverlaySymbolState(overlaySymbols);
        this.setJackpotSymbols(overlaySymbols, jackpotSymbol);

        if (nextActions && !haveEnteredFreeSpins) {
            this.freeSpinSymbols = this.vsSymbols;
        }
    }

    processFreeSpinEvent(event: FreeSpinEvent): void {

        const {gameplay, nextActions} = event.body.data;
        const {dragonsReels, oldDragonsReels, pandasReels, oldPandasReels, dragonsPaylineWins, pandasPaylineWins,
               overlaySymbols, jackpotSymbol, dragonsWilds, pandasWilds, bonusTriggeringState} = gameplay;

        if (bonusTriggeringState) {
            this.setBonusTriggeringState(bonusTriggeringState);
        }

        //Set pre jumping wilds result
        this.dragonsSpinResult = oldDragonsReels[0];
        this.pandasSpinResult = oldPandasReels[0];

        this.setOverlaySymbolState(overlaySymbols);
        this.setJumpingWilds(oldDragonsReels[0], dragonsReels, oldPandasReels[0], pandasReels, dragonsWilds, pandasWilds);
        this.setJackpotSymbols(overlaySymbols, jackpotSymbol);
        this.setVSSymbols(overlaySymbols, (nextActions) ? nextActions[0] : undefined);
        this.setWinningSymbolState(dragonsPaylineWins, pandasPaylineWins);
    }

    setOverlaySymbolState(overlaySymbolData: OverlaySymbolData[]): void {

        this.clearOverlaySymbolState();

        let set;

        for (const item of overlaySymbolData) {

            set = (item.board === `DRAGONS_BOARD`) ? this.overlaySymbolState.dragons : this.overlaySymbolState.pandas;
            set[item.position.column][item.position.row] = item.type;
        }
    }

    getOverlaySymbolState(overlaySymbolData: OverlaySymbolData[]): OverlaySymbolState {

        const newState: OverlaySymbolState = this.overlaySymbolEmptyState;
        let set: ReelSetOverlays;

        for (const item of overlaySymbolData) {

            set = (item.board === `DRAGONS_BOARD`) ? newState.dragons : newState.pandas;
            set[item.position.column][item.position.row] = item.type;
        }

        return newState;
    }

    setBonusOverlaySymbolState(overlaySymbolData: OverlaySymbolData[]): void {

        this.clearOverlaySymbolState();

        let set;

        if (this._bonusTriggeringState) {
            for (const item of overlaySymbolData) {

                set = (item.board === `DRAGONS_BOARD`) ? this._bonusTriggeringState.overlays.dragons : this._bonusTriggeringState.overlays.pandas;
                set[item.position.column][item.position.row] = item.type;
            }
        }
    }

    setWinningSymbolState(dragonsPaylineWins: PayLineWin[], pandasPaylineWins: PayLineWin[]): void {

        const {reelCount, reelsPerSet} = this.layout;

        this.winningSymbolState = times(reelCount, () => [false,false,false,false]);

        for (const win of dragonsPaylineWins) {
            for (const entry of win.pattern) {
                this.winningSymbolState[entry.column][entry.row] = true;
            }
        }

        for (const win of pandasPaylineWins) {
            for (const entry of win.pattern) {
                this.winningSymbolState[entry.column + reelsPerSet][entry.row] = true;
            }
        }
    }

    clearOverlaySymbolState(): void {
        this.overlaySymbolState = this.overlaySymbolEmptyState;
    }

    applyBonusOverlaySymbols(): void {
        if (this.scatterOverlaySymbols) {
            this.setBonusOverlaySymbolState(this.scatterOverlaySymbols);
        }

    }

    override reset(): void {

        this.dragonsSpinResult = undefined;
        this.pandasSpinResult = undefined;

        this.dragonsVSSpinResults = [];
        this.pandasVSSpinResults = [];

        this.overlaySymbolState = this.overlaySymbolEmptyState;
        this.jumpingWilds = {dragons: [], pandas: []};
        this.winningSymbolState = [];
        this.startTime = 0;
    }

    private calcReelPositionsForSet(spec: ReelsSpec, reelCount: number): ReelSetPositions {

        const positions = [];

        const {reelSpacing, symbolSize} = spec;

        for (let i=0; i<reelCount; i++) {

            positions.push(
                {
                    x: i * symbolSize.width + i * reelSpacing + symbolSize.width/2,
                    y: symbolSize.height/2 + symbolSize.height/2
                }
            );
        }

        return positions as ReelSetPositions;
    }

    private calcSymbolPositionsForSet(spec: ReelsSpec, setOffset: Point, reelCount: number, symbolCount: number,
        setPositions: ReelSetPositions): Point[][] {

        const globalSymbolPositions = [];

        const {symbolSize} = spec;

        for (let i=0; i<reelCount; i++) {

            const symbolPositions = [];

            for (let j=0; j<symbolCount; j++) {

                symbolPositions.push(
                    {
                        x: spec.x + setOffset.x + setPositions[i].x,
                        y: spec.y + setOffset.y + setPositions[i].y + (symbolSize.height * (j+1)) - symbolSize.height/2.0
                    }
                );
            }

            globalSymbolPositions.push(symbolPositions);
        }

        return globalSymbolPositions;
    }

    private setupSpinConfigs(): void {

        const settings: Record<SpinType, SpinSettings> = cloneDeep(this.services.resources.getSetting(`reelSpinSettings`));

        for (const [key, value] of Object.entries(settings)) {

            switch(key) {
                case SpinType.REGULAR:
                    this.defaultSpinConfigs.set(key, {...value, anticipationActive: false});
                    break;
                case SpinType.VS:
                case SpinType.FREE_SPIN:
                case SpinType.TURBO:
                case SpinType.TURBO_VS:
                case SpinType.TURBO_FREE_SPIN:
                    this.defaultSpinConfigs.set(key, this.makeCustomSpinConfig(settings[SpinType.REGULAR], value));
                    break;
                default:
                    throw new Error(`Unknown ReelSpinSettings ${key}`);
            }
        }

        this.spinConfigs = cloneDeep(this.defaultSpinConfigs);
    }

    private makeCustomSpinConfig(defaultValues: Record<string,any>, customValues: Record<string,any>): SpinConfig {

        const customConfig = cloneDeep(defaultValues);

        for (const [key, value] of Object.entries(customValues)) {
            customConfig[key] = value;
        }

        customConfig.anticipationActive = false;

        return customConfig as SpinConfig;
    }

    setJumpingWilds(oldDragonsReels: ReelSetSymbols, dragonsReels: ReelSetSymbols, oldPandasReels: ReelSetSymbols,
        pandasReels: ReelSetSymbols, dragonsWilds: MultipliedWildsData, pandasWilds: MultipliedWildsData): void {

        const {reelsPerSet} = this.layout;

        const dragonsJumpingWilds = times(reelsPerSet, () => [] as number[]);
        const pandasJumpingWilds = times(reelsPerSet, () => [] as number[]);

        const dragonsNewWilds = times(reelsPerSet, () => [] as number[]);
        const pandasNewWilds = times(reelsPerSet, () => [] as number[]);

        //A wild from each of these locations is moving to a position on the other board
        for (const [i, symbols] of oldDragonsReels.entries()) {
            for (const [j, item] of symbols.entries()) {

                if (item === `WW`) {
                    dragonsJumpingWilds[j].push(i);
                }
            }
        }

        for (const [i, symbols] of oldPandasReels.entries()) {
            for (const [j, item] of symbols.entries()) {

                if (item === `WW`) {
                    pandasJumpingWilds[j].push(i);
                }
            }
        }

        //New wild locations based on the difference between the old and new reels
        for (const [i, symbols] of dragonsReels.entries()) {
            for (const [j, item] of symbols.entries()) {

                if (item === `WW` && oldDragonsReels[i][j] !== `WW`) {
                    dragonsNewWilds[j].push(i);
                }
            }
        }

        for (const [i, symbols] of pandasReels.entries()) {
            for (const [j, item] of symbols.entries()) {

                if (item === `WW` && oldPandasReels[i][j] !== `WW`) {
                    pandasNewWilds[j].push(i);
                }
            }
        }

        const dragonsMultipliedWilds = this.extractMultipliedWildsData(dragonsWilds);
        const pandasMultipliedWilds = this.extractMultipliedWildsData(pandasWilds);

        //Add additional new wild locations which are duplicates
        for (const item of dragonsMultipliedWilds) {

            for (let i=item.count; i>1; i--) {
                dragonsNewWilds[item.position.row].push(item.position.column);
            }
        }

        for (const item of pandasMultipliedWilds) {

            for (let i=item.count; i>1; i--) {
                pandasNewWilds[item.position.row].push(item.position.column);
            }
        }

         this.jumpingWilds = {
            dragons: this.constructJumpingWildsSet(dragonsJumpingWilds, pandasNewWilds, pandasMultipliedWilds, 0, reelsPerSet),
            pandas:this.constructJumpingWildsSet(pandasJumpingWilds, dragonsNewWilds, dragonsMultipliedWilds, reelsPerSet, 0)
        };
    }

    private extractMultipliedWildsData(wilds: MultipliedWildsData): MultipliedWilds {

        const set = [];

        let column, row;

        for (const [key, count] of Object.entries(wilds)) {

            column = parseInt(key.charAt(16));
            row = parseInt(key.charAt(23));

            set.push({
                position: {column, row},
                count
            });
        }

        return set;
    }

    private constructJumpingWildsSet(sourceWilds: number[][], targetWilds: number[][], multipliedWilds: MultipliedWilds,
        sourceOffset=0, targetOffset=0): JumpingWildsSet {

        const set = [];

        let targetColumn: number, multiplier;

        for (const [sourceRow, columns] of sourceWilds.entries()) {
            for (const sourceColumn of columns) {

                targetColumn = targetWilds[sourceRow].splice(random(0, targetWilds[sourceRow].length-1), 1)[0];

                multiplier = multipliedWilds.filter((item) => {
                    return (item.position.row === sourceRow && item.position.column === targetColumn);
                }).map((item) => item.count)[0];

                set.push({
                    source: {column: sourceColumn + sourceOffset, row: sourceRow},
                    target: {column: targetColumn + targetOffset, row: sourceRow},
                    multiplier
                });
            }
        }

        return set;
    }

    private setJackpotSymbols(overlaySymbolData: OverlaySymbolData[], jackpotSymbol?: JackpotID): void {

        if (jackpotSymbol) {

            const dragons = [];
            const pandas = [];

            for (const item of overlaySymbolData) {

                if (item.board === `DRAGONS_BOARD`) {
                    dragons.push({...item.position});

                } else {
                    pandas.push({...item.position});
                }
            }

            this.jackpotSymbols = {dragons, pandas};
        } else {
            this.jackpotSymbols = undefined;
        }
    }

    private setVSSymbols(overlaySymbolData: OverlaySymbolData[], nextEvent?: GameRequest): void {

        if (nextEvent === `VS_SPIN`) {

            const dragons = [];
            const pandas = [];

            for (const item of overlaySymbolData) {

                if (item.board === `DRAGONS_BOARD`) {
                    dragons.push({...item.position});

                } else {
                    pandas.push({...item.position});
                }
            }

            this.vsSymbols = {dragons, pandas};
        } else {
            this.vsSymbols = undefined;
        }
    }

    private setFreeSpinSymbols(overlaySymbolData: OverlaySymbolData[], nextEvent?: GameRequest): void {

        if (nextEvent === `FREE_SPIN`) {

            const dragons = [];
            const pandas = [];

            for (const item of overlaySymbolData) {

                if (item.board === `DRAGONS_BOARD`) {
                    dragons.push({...item.position});

                } else {
                    pandas.push({...item.position});
                }
            }

            this.freeSpinSymbols = {dragons, pandas};
        } else {

            let doubleBonus = false;
            for (const item of overlaySymbolData) {

                if (item.type === `FG`) {

                    doubleBonus = true;
                    break;
                }
            }

            this.doubleBonus = doubleBonus;
            this.freeSpinSymbols = undefined;
        }
    }

    private setBonusTriggeringState(bonusTriggeringState: BonusTriggeringState): void {

        const {freeGamesState, dragonsReels, pandasReels, oldDragonsReels, oldPandasReels, overlaySymbols, dragonsPaylineWins, pandasPaylineWins, dragonsMultipliedWins, pandasMultipliedWins, dragonsWilds, pandasWilds} = bonusTriggeringState;

        this._bonusTriggeringState = {
            freeGamesState: undefined,
            oldSymbols: {
                dragons: oldDragonsReels,
                pandas: oldPandasReels
            },
            symbols: {
                dragons: dragonsReels,
                pandas: pandasReels
            },
            overlays: this.getOverlaySymbolState(overlaySymbols),
            paylines: {
                dragons: dragonsPaylineWins !== undefined ? dragonsPaylineWins : [],
                pandas: pandasPaylineWins !== undefined ? pandasPaylineWins : [],
            },
            multipliedWinsMap: {
                dragons: dragonsMultipliedWins,
                pandas: pandasMultipliedWins
            },
            multipliedWilds: {
                dragons: dragonsWilds ? dragonsWilds : {},
                pandas: pandasWilds ? pandasWilds : {}
            }
        };

        if (freeGamesState) {
            const {dragonsReels, pandasReels, oldDragonsReels, oldPandasReels, overlaySymbols, dragonsPaylineWins, pandasPaylineWins, dragonsMultipliedWins, pandasMultipliedWins, dragonsWilds, pandasWilds} = freeGamesState;
            this._bonusTriggeringState.freeGamesState = {
                oldSymbols: {
                    dragons: oldDragonsReels,
                    pandas: oldPandasReels
                },
                symbols: {
                    dragons: dragonsReels,
                    pandas: pandasReels
                },
                overlays: this.getOverlaySymbolState(overlaySymbols),
                paylines: {
                    dragons: dragonsPaylineWins !== undefined ? dragonsPaylineWins : [],
                    pandas: pandasPaylineWins !== undefined ? pandasPaylineWins : [],
                },
                multipliedWinsMap: {
                    dragons: dragonsMultipliedWins,
                    pandas: pandasMultipliedWins
                },
                multipliedWilds: {
                    dragons: dragonsWilds ? dragonsWilds : {},
                    pandas: pandasWilds ? pandasWilds : {}
                }
            };
        }
    }

    private setVSResults(dragonsReels: ReelSetSymbols, pandasReels: ReelSetSymbols, oldDragonsReels?: ReelSetSymbols[],
        oldPandasReels?: ReelSetSymbols[]): void {

        //set to first result for slam stop
        this.dragonsSpinResult = oldDragonsReels ? oldDragonsReels[0] : dragonsReels;
        this.pandasSpinResult = oldPandasReels ? oldPandasReels[0] : pandasReels;

        this.dragonsVSSpinResults = [];
        this.pandasVSSpinResults = [];

        if (oldDragonsReels) {

            for (const item of oldDragonsReels) {
                this.dragonsVSSpinResults.push(item);
            }
        }

        this.dragonsVSSpinResults.push(dragonsReels);

        if (oldPandasReels) {

            for (const item of oldPandasReels) {
                this.pandasVSSpinResults.push(item);
            }
        }

        this.pandasVSSpinResults.push(pandasReels);
    }

    private setVSOverlaySymbolState(overlaySymbolData: OverlaySymbolData[]): void {

        //overlays are only valid for the final reel result, so clear if there is a respin
        if (this.dragonsVSSpinResults.length !== this.pandasVSSpinResults.length) {
            this.clearOverlaySymbolState();
        } else {
            this.setOverlaySymbolState(overlaySymbolData);
        }
    }
}